import './lib/i18n'
// Import to register module-level event handlers (booking:synced, booking:sync-failed → Dexie updates)
import './lib/store/useBookingStore'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'sonner'
import './index.css'
import App from './App'
import { initializeTheme, useThemeStore } from './lib/store/useThemeStore'

initializeTheme()

if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/pos-sw.js')
      .then(async () => {
        const registration = await navigator.serviceWorker.ready
        const urls = ['/', '/index.html', ...performance.getEntriesByType('resource').map((entry) => entry.name)]
        registration.active?.postMessage({ type: 'CACHE_POS_SHELL', urls })
      })
      .catch((error) => {
        console.error('POS offline shell registration failed', error)
      })
  })
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      // Keep in-memory cache for a while; Dexie is now the persistence layer
      gcTime: 5 * 60_000,
      staleTime: 0,
      // Don't throw on network failure — Dexie will serve data instead
      networkMode: 'always',
    },
    mutations: {
      networkMode: 'always',
    },
  },
})

function ThemedToaster() {
  const resolvedTheme = useThemeStore((s) => s.resolvedTheme)
  return (
    <Toaster
      position="bottom-right"
      theme={resolvedTheme}
      toastOptions={{
        style: {
          background: 'var(--color-bg-elevated)',
          border: '1px solid var(--color-border-subtle)',
          color: 'var(--color-text-primary)',
        },
      }}
    />
  )
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
      <ThemedToaster />
    </QueryClientProvider>
  </StrictMode>,
)
